import { AdOverlapResolution, DeviceTypes } from '@shared/api/graphql/generated';
import { PlayerSupportedFeatures } from 'application/pages/constants';
import { CUT_DURATIONS } from './consts';
import { AdvertisingIntent, AdvertisingMode, AdvertisingPoint, AdvertisingSettings } from './types';

export const getBindingIdentity = ({ id, device }: AdvertisingPoint): string =>
  JSON.stringify([id, device?.id, device?.bindedAt]);

export const getAdvertisingMode = (point: AdvertisingPoint, previous?: AdvertisingMode): AdvertisingMode => {
  const { device } = point;
  if (!device?.bindedAt) return 'unrestricted';
  if (device.type === DeviceTypes.BoomSpeaker) return 'basic';
  if (!Array.isArray(device.supportedFeatures)) return previous || 'unrestricted';
  return device.supportedFeatures.includes(PlayerSupportedFeatures.OverlapResolution) ? 'independent' : 'coupled';
};

export const getAdvertisingSettings = (point: AdvertisingPoint): AdvertisingSettings | undefined => {
  const { withMusicCut, cutDuration, adOverlapResolution } = point;
  if (typeof withMusicCut !== 'boolean' || typeof cutDuration !== 'number' || !adOverlapResolution) return undefined;
  return { withMusicCut, cutDuration, adOverlapResolution };
};

export const isAdvertisingIntentAllowed = (
  mode: AdvertisingMode,
  settings: AdvertisingSettings,
  intent: AdvertisingIntent
): boolean => {
  if (intent.field === 'cutDuration') return CUT_DURATIONS.some((duration) => duration === intent.value);
  if (intent.field === 'withMusicCut') return mode !== 'basic' || !intent.value;
  if (mode === 'basic') return intent.value === AdOverlapResolution.Shift;
  if (mode === 'coupled') {
    return intent.value === (settings.withMusicCut ? AdOverlapResolution.Drop : AdOverlapResolution.Shift);
  }
  return true;
};

export const getAdvertisingPatch = (
  mode: AdvertisingMode,
  settings: AdvertisingSettings,
  intent: AdvertisingIntent
): Partial<AdvertisingSettings> | undefined => {
  if (!isAdvertisingIntentAllowed(mode, settings, intent) || settings[intent.field] === intent.value) return undefined;
  if (intent.field === 'withMusicCut' && mode === 'coupled') {
    return {
      withMusicCut: intent.value,
      adOverlapResolution: intent.value ? AdOverlapResolution.Drop : AdOverlapResolution.Shift,
    };
  }
  return { [intent.field]: intent.value };
};
